import { useMemo, useState } from "react";
import { FileText } from "lucide-react";
import { FSSAI_LICENCES } from "../data/legalRegistrations";
import { buildCertificateFilename } from "../utils/certificateFilename";

export default function FssaiDirectory() {
  const states = useMemo(
    () => Array.from(new Set(FSSAI_LICENCES.map((l) => l.state))).sort(),
    []
  );
  const [stateFilter, setStateFilter] = useState<string>("All");

  const grouped = useMemo(() => {
    const filtered = stateFilter === "All" ? FSSAI_LICENCES : FSSAI_LICENCES.filter((l) => l.state === stateFilter);
    const byState = new Map<string, typeof FSSAI_LICENCES>();
    for (const licence of filtered) {
      const bucket = byState.get(licence.state) ?? [];
      bucket.push(licence);
      byState.set(licence.state, bucket);
    }
    return Array.from(byState.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [stateFilter]);

  if (FSSAI_LICENCES.length === 0) {
    return (
      <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-ocean-900/5">
        <h3 className="font-display font-semibold text-ocean-950">FSSAI Licences</h3>
        <p className="mt-2 text-sm text-ocean-800/60">
          Our published FSSAI licence directory will appear here once registrations are finalized.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-ocean-900/5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-display font-semibold text-ocean-950">FSSAI Licences</h3>
        <select
          value={stateFilter}
          onChange={(e) => setStateFilter(e.target.value)}
          aria-label="Filter by state"
          className="rounded-full border border-ocean-900/10 bg-foam-50 px-3 py-1.5 text-xs font-medium text-ocean-800"
        >
          <option value="All">All states</option>
          {states.map((state) => (
            <option key={state} value={state}>
              {state}
            </option>
          ))}
        </select>
      </div>

      <div className="mt-4 space-y-6">
        {grouped.map(([state, licences]) => (
          <div key={state}>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-teal-600">{state}</h4>
            <ul className="mt-2 space-y-3">
              {licences.map((licence) => (
                <li key={licence.id} className="rounded-xl bg-foam-50 p-4 text-sm">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="font-mono text-ocean-950">{licence.licenceNumber}</span>
                    <span className="rounded-full bg-ocean-900/5 px-2 py-0.5 text-xs font-medium text-ocean-800">
                      {licence.licenceType}
                    </span>
                  </div>
                  <p className="mt-1.5 text-xs text-ocean-800/60">
                    Issued by {licence.issuingAuthority} · Valid {licence.validFrom} – {licence.validTo}
                  </p>
                  {licence.certificateUrl && (
                    <a
                      href={licence.certificateUrl}
                      target="_blank"
                      rel="noreferrer"
                      download={buildCertificateFilename(licence.licenceType, licence.validTo.slice(0, 4))}
                      className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-teal-600 hover:text-teal-700"
                    >
                      <FileText className="h-3.5 w-3.5" />
                      View Certificate
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}
